"use client";

/**
 * Resolve the active page-wide IANA time zone for the current rendering surface.
 *
 * Both the builder canvas and the public renderer carry a `timeZone` field. Renderers that
 * mount in either context (e.g. `ActivityCard`, `HeroRenderer`) used to do this dispatch
 * inline; centralizing keeps the precedence rule in one spot:
 *  - In the public view, `PublicViewContext` is the source of truth.
 *  - Otherwise the builder context applies.
 *
 * `usePublicViewContext` returns a default `{ isPublic: false, timeZone: null, ... }` shape
 * when no public provider is mounted, so reading from it is safe inside the builder.
 */

import { useBuilderContext } from "./useBuilderContext";
import { usePublicViewContext } from "../_components/public/PublicViewContext";

/**
 * @returns The IANA zone for the active surface, or null when unset (floating time).
 */
export function useTimeZone(): string | null {
  const { isPublic, timeZone: publicTimeZone } = usePublicViewContext();
  const { timeZone: builderTimeZone } = useBuilderContext();
  return isPublic ? publicTimeZone : builderTimeZone;
}
